import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

export function AddCandidateSection() {
  return (
    <GuideSection
      id="add-candidate"
      eyebrow="Candidates"
      title="Add your first candidate"
      description="Every person you coach lives in the Candidates list. Add them once, reuse them across interviews and tests."
    >
      <GuideScreenshot
        src="/guides/candidates-empty.png"
        alt="Candidates page empty state — search bar across the top, 'Add candidate' button top right, 'No candidates yet' placeholder card in the middle"
        caption="A fresh Candidates page. Click Add candidate (top right) to open the intake form."
      />

      <GuideScreenshot
        src="/guides/add-candidate-dialog.png"
        alt="Add candidate dialog with fields for full name, email, role/job title, and notes"
        caption="The dialog asks for name + email (required), plus optional role and free-text notes. Required fields are starred."
      />

      <GuideScreenshot
        src="/guides/candidates-list.png"
        alt="Candidates page with one candidate card showing initials, name, email, role, target company, and quick-action Interview and Test buttons"
        caption="Once added, each candidate gets a card with Interview and Test quick-actions that jump straight into the scheduling wizard."
      />

      <GuideSteps>
        <GuideStep number={1} title="Open Candidates">
          <p>
            Click <strong>Candidates</strong> in the sidebar. On a fresh tenant this list starts
            empty.
          </p>
          <GuideAction href="/end-clients">Open Candidates</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Click “Add candidate”">
          <p>The button sits in the top-right of the page header. It opens a dialog.</p>
        </GuideStep>

        <GuideStep number={3} title="Fill the form">
          <p>Required fields:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Name</strong> — what you want to see in lists and emails.
            </li>
            <li>
              <strong>Email</strong> — must be unique inside your tenant; this is how NeuraX routes
              interview sessions from the desktop app.
            </li>
          </ul>
          <p>Optional but helpful:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Job title</strong> and <strong>target company</strong> — pre-fills the
              interview wizard later.
            </li>
            <li>
              <strong>LinkedIn URL</strong> and <strong>internal notes</strong> — for your own
              records; candidates never see these.
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={4} title="Submit">
          <p>
            Hit <strong>Add candidate</strong>. The candidate appears in the list immediately, and
            NeuraX dispatches an <strong>onboarding email</strong> to them containing your company
            code and the NeuraX desktop app download link.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="info" title="What the candidate receives">
        <p>
          An email from your tenant's sender address with: a welcome message, your company code, and
          a link to download NeuraX. They can install the app right away; once their license is
          ready (issued during interview scheduling) it automatically lights up in their app.
        </p>
      </GuideCallout>

      <GuideCallout variant="tip" title="Bulk onboarding">
        <p>
          The portal adds one candidate per dialog. For larger rosters, work with your F1Jobs
          contact — we can either do a bulk import on your behalf or expose a CSV uploader.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Duplicate emails">
        <p>
          If a candidate with the same email already exists (case-insensitive) in your tenant, the
          dialog will show <em>"A candidate with this email already exists"</em>. Use the existing
          record instead.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
